import {Grid, Stack} from '@primer/react-brand'
import PricingCard from './PricingCard'
import {analyticsEvent} from '../../../../lib/analytics'

interface PricingCardsNewProps {
  siteCopilotPurchaseRefresh: boolean
  copilotSignupPath: string
  copilotForBusinessSignupPath: string
  copilotForBusinessSignupPathRefresh: string
  copilotEnterpriseSignupPath: string
  copilotContactSalesPath: string
  userHasOrg: boolean
  isLoggedIn: boolean
}

export default function PricingCardsNew({
  siteCopilotPurchaseRefresh,
  copilotSignupPath,
  copilotForBusinessSignupPath,
  copilotForBusinessSignupPathRefresh,
  copilotEnterpriseSignupPath,
  copilotContactSalesPath,
  userHasOrg,
  isLoggedIn,
}: PricingCardsNewProps) {
  const copilotBusinessContactSalesPath = `${copilotContactSalesPath}&utm_content=CopilotBusiness`
  const copilotEnterpriseContactSalesPath = `${copilotContactSalesPath}&utm_content=CopilotEnterprise`

  interface AnalyticsEvent {
    action: string
    tag: string
    context: string
    location: string
  }

  interface CTA {
    text: string
    href: string
    variant: 'primary' | 'secondary'
    analyticsEvent: AnalyticsEvent
  }

  interface PricingCardContent {
    label?: string
    heading: string
    description: string
    price: string
    priceDescription?: string | JSX.Element
    disclaimer?: string | JSX.Element
    ctas: CTA[]
  }

  const cardData: PricingCardContent[] = [
    {
      label: 'New',
      heading: 'Free',
      description: 'For developers looking to get started with GitHub Copilot.',
      price: '0',
      disclaimer: 'Includes up to 2,000 completions and 50 chat requests per month.',
      ctas: [
        {
          text: isLoggedIn ? 'Use in GitHub' : 'Start for free',
          href: 'https://github.com/copilot',
          variant: 'primary',
          analyticsEvent: {
            action: 'start_trial',
            tag: 'button',
            context: 'pro_plan',
            location: 'offer_cards',
          },
        },
      ],
    },
    {
      label: !userHasOrg ? 'Most popular' : undefined,
      heading: 'Pro',
      description: 'For developers who want unlimited access to GitHub Copilot.',
      price: '10',
      priceDescription: 'per month / $100 USD per year',
      disclaimer: (
        <>
          Free for verified students, teachers, and maintainers of popular open source projects.{' '}
          <a
            href="https://github.com/education"
            className="lp-Link--inline"
            {...analyticsEvent({
              action: 'learn_more',
              tag: 'link',
              context: 'education_pro_plan',
              location: 'offer_cards',
            })}
          >
            Learn more
          </a>
        </>
      ),
      ctas: [
        {
          text: 'Start a free trial',
          href: copilotSignupPath,
          variant: 'primary',
          analyticsEvent: {
            action: 'start_trial',
            tag: 'button',
            context: 'pro_plan',
            location: 'offer_cards',
          },
        },
      ],
    },
    {
      label: userHasOrg ? 'Popular for teams' : undefined,
      heading: 'Business',
      description: 'For teams ready to accelerate their workflows with GitHub Copilot.',
      price: '19',
      priceDescription: 'per user / month',
      ctas: [
        {
          text: 'Buy now',
          href: siteCopilotPurchaseRefresh ? copilotForBusinessSignupPathRefresh : copilotForBusinessSignupPath,
          variant: 'primary',
          analyticsEvent: {
            action: 'buy_now',
            tag: 'button',
            context: 'business_plan',
            location: 'offer_cards',
          },
        },
        {
          text: 'Contact sales',
          href: copilotBusinessContactSalesPath,
          variant: 'secondary',
          analyticsEvent: {
            action: 'contact_sales',
            tag: 'button',
            context: 'business_plan',
            location: 'offer_cards',
          },
        },
      ],
    },
    {
      heading: 'Enterprise',
      description: 'For organizations seeking a fully customized GitHub Copilot experience.',
      price: '39',
      priceDescription: 'per user / month',
      ctas: [
        {
          text: 'Buy now',
          href: copilotEnterpriseSignupPath,
          variant: 'primary',
          analyticsEvent: {
            action: 'buy_now',
            tag: 'button',
            context: 'enterprise_plan',
            location: 'offer_cards',
          },
        },
        {
          text: 'Contact sales',
          href: copilotEnterpriseContactSalesPath,
          variant: siteCopilotPurchaseRefresh ? 'secondary' : 'primary',
          analyticsEvent: {
            action: 'contact_sales',
            tag: 'button',
            context: 'enterprise_plan',
            location: 'offer_cards',
          },
        },
      ],
    },
  ]

  if (!siteCopilotPurchaseRefresh && cardData[3] && cardData[3].ctas[0]) {
    delete cardData[3].ctas[0]
  }

  return (
    <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap text-center position-relative mt-0">
      <Grid.Column span={12} className="position-relative">
        <Grid className="lp-Pricing">
          <Grid.Column span={12} className="px-0 py-0">
            <Stack
              direction={{narrow: 'vertical', regular: 'horizontal'}}
              gap={{narrow: 48, regular: 16}}
              padding="none"
              className="lp-Pricing-cards"
            >
              {cardData.map(content => (
                <PricingCard key={content.heading} content={content} />
              ))}
            </Stack>
          </Grid.Column>
        </Grid>
      </Grid.Column>
    </Grid>
  )
}

try{ PricingCardsNew.displayName ||= 'PricingCardsNew' } catch {}